import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Aperture, Loader2, Mail } from "lucide-react";
import { toast } from "sonner";
import { getBrand } from "@/lib/branding";

const AUTH_BG =
  "https://images.unsplash.com/photo-1693648793394-0b76b7eb042e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGRhcmslMjBtb2Rlcm4lMjB0ZXh0dXJlfGVufDB8fHx8MTc4NDMzMjA5Nnww&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [busy, setBusy] = useState(false);
  const brand = getBrand();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const path = mode === "login" ? "/auth/login" : "/auth/register";
      const payload =
        mode === "login"
          ? { email: form.email, password: form.password }
          : { email: form.email, password: form.password, name: form.name };
      const { data } = await api.post(path, payload);
      setUser(data);
      toast.success(mode === "login" ? "Welcome back" : "Account created");
      navigate(data?.is_member ? "/my-tasks" : "/dashboard");
    } catch (err) {
      toast.error(apiError(err, "Authentication failed"));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="grid min-h-screen bg-background lg:grid-cols-2">
      {/* Left visual */}
      <div className="relative hidden overflow-hidden lg:block">
        <img src={AUTH_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/70" />
        <div className="grain absolute inset-0 opacity-60" />
        <div className="relative flex h-full flex-col justify-between p-12">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-primary/20 ring-1 ring-primary/40">
              {brand.logo ? <img src={brand.logo} alt="logo" className={`h-full w-full object-contain ${brand.invert ? "dark:invert" : ""}`} /> : <Aperture className="h-5 w-5 text-primary" />}
            </div>
            <span className="font-heading text-lg font-extrabold tracking-tight">{brand.name || "HomePix Ops"}</span>
          </div>
          <div className="max-w-md">
            <div className="overline mb-4 text-primary">Real Estate Media Studio</div>
            <h1 className="font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl">
              Every shoot, edit &amp; delivery in one command center.
            </h1>
            <p className="mt-5 text-base leading-relaxed text-muted-foreground">
              Sync your Aryeo orders, run your production pipeline, and track every deliverable — video,
              reels, floor plans, 3D tours and more.
            </p>
          </div>
          <div className="overline text-muted-foreground">Powered by the Aryeo API</div>
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-sm animate-fade-up">
          <div className="mb-8 flex items-center gap-3 lg:hidden">
            <div className="flex h-10 w-10 items-center justify-center overflow-hidden rounded-lg bg-primary/20 ring-1 ring-primary/40">
              {brand.logo ? <img src={brand.logo} alt="logo" className={`h-full w-full object-contain ${brand.invert ? "dark:invert" : ""}`} /> : <Aperture className="h-5 w-5 text-primary" />}
            </div>
            <span className="font-heading text-lg font-extrabold tracking-tight">{brand.name || "HomePix Ops"}</span>
          </div>

          <h2 className="font-heading text-2xl font-bold tracking-tight">
            {mode === "login" ? "Sign in" : "Create account"}
          </h2>
          <p className="mt-1.5 text-sm text-muted-foreground">
            {mode === "login" ? "Access your media production dashboard." : "Start managing your workflow today."}
          </p>

          <Button
            type="button"
            variant="outline"
            onClick={googleLogin}
            data-testid="google-login-btn"
            className="mt-6 w-full gap-2 border-border bg-secondary/50 hover:bg-secondary"
          >
            <svg className="h-4 w-4" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z" />
              <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84Z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.06l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z" />
            </svg>
            Continue with Google
          </Button>

          <div className="my-6 flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="overline text-muted-foreground">or</span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <form onSubmit={submit} className="space-y-4">
            {mode === "register" && (
              <div className="space-y-1.5">
                <Label htmlFor="name">Name</Label>
                <Input id="name" data-testid="name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Jane Doe" />
              </div>
            )}
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input id="email" data-testid="email-input" type="email" required value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@studio.com" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" data-testid="password-input" type="password" required value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="••••••••" />
            </div>
            <Button type="submit" disabled={busy} data-testid="submit-auth-btn" className="w-full gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
              {mode === "login" ? "Sign in" : "Create account"}
            </Button>
          </form>

          <p className="mt-6 text-center text-sm text-muted-foreground">
            {mode === "login" ? "New to HomePix Ops?" : "Already have an account?"}{" "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => setMode(mode === "login" ? "register" : "login")}
              className="font-medium text-primary transition-colors hover:text-primary/80"
            >
              {mode === "login" ? "Create one" : "Sign in"}
            </button>
          </p>
        </div>
      </div>
    </div>
  );
}
